import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { useModalDimensions } from '@hooks/index';
import { useTranslation } from 'react-i18next';

interface FindSpecialistModalProps {
	open: boolean;
	onClose: () => void;
	description: string;
}

const oneTimeContent = {
	ka: {
		title: 'ერთჯერადი კონსულტაციის სერვისის აღწერა',
		description:
			'ერთჯერადი კონსულტაცია არის სწრაფი და ეფექტური გზა აგრონომებთან კვალიფიციური რეკომენდაციების მისაღებად. სერვისი ფარგლებში მომხმარებელი კონსულტაციას იღებს სპეციალისტისგან, კონკრეტული საჭიროების შესახებ. საკითხი შეიძლება იყოს უკვე არსებულ პრობლემასთან დაკავშირებული ან პრევენციული ღონისძიების შესახებ.',
		howItWorks: 'როგორ მუშაობს სერვისი?',
		steps: [
			'ფერმერი პლატფორმაზე ირჩევს ერთჯერადი კონსულტაციის სერვისს, უთითებს კულტურას და იმ ენას, რა ენაზეც სურს კონსულტაციის მიღება.',
			'შემდეგ მაქსიმალურად დეტალურად აღწერს პრობლემას ან საკითხის შინაარსს, რომელზეც სჭირდება კონსულტაცია.',
			'მომხმარებლის მოთხოვნა, კულტურის და ენის ფილტრის გათვალისწინებით, ეგზავნებათ პლატფორმაზე არსებულ ყველა სპეციალისტს.',
			'მოთხოვნის გაგზავნა ასევე შესაძლებელია პირდაპირ კონკრეტულ სპეციალისტთან, სპეციალური ინდივიდუალური კოდის მეშვეობით.',
			'სპეციალისტები შეაფასებენ მოთხოვნის სირთულეს და აგზავნიან შესაბამის შეთავაზებებს.',
			'ერთჯერადი კონსულტაციის ღირებულებას განსაზღვრავს თავად აგრონომი.',
			'ფერმერი თავად ირჩევს სპეციალისტს მისი გამოცდილებისა და შეფასებების მიხედვით.',
			'თანხას იხდით მხოლოდ მას შემდეგ, რაც მომხმარებელი აირჩევს სასურველ სპეციალისტს.',
			'მომხმარებელს და სპეციალისტს აქვთ უფლება წინასწარ, შეთავაზების დადასტურებამდე, ჰქონდეთ სერვისის შესახებ კომუნიკაცია ტელაგრის ჩატში, სერვისის ფარგლებში სხვადასხვა დეტალების დასაზუსტებლად.',
			'შესაძლებელია მომხმარებელმა სპეციალისტებისგან მიიღოს უფასო კონსულტაციის შეთავაზება. ასეთი შეთავაზების დათანხმების შემთხვევაში მომხმარებელი არაფერს არ იხდის.',
			'შერჩეულ აგრონომთან კომუნიკაცია ტელაგრის ჩატის მეშვეობით შესაძლებელია სერვისის ყიდვიდან 5 დღის განმავლობაში.',
			'კონსულტაცია სრულდება, ავტომატურად 5 დღეში.',
		],
	},
	en: {
		title: 'One-Time Consultation Service',
		description:
			'A one-time consultation is a quick and effective way to receive qualified recommendations from agronomists. Through this service, users can consult a specialist regarding their specific needs. The consultation may address an existing problem or focus on preventive measures.',
		howItWorks: 'How Does the Service Work?',
		steps: [
			'The farmer selects the one-time consultation service on the platform, specifies the crop, and chooses the preferred language(s) for the consultation.',
			'Next, they provide a detailed description of the problem or the topic they need consultation on.',
			'The request is then sent to all specialists on the platform, filtered based on crop and language preferences.',
			'Alternatively, the request can be sent directly to a specific specialist using a unique individual code.',
			'Specialists assess the complexity of the request and submit relevant offers.',
			'The price for a one-time consultation is determined by the agronomist.',
			'The farmer selects a specialist based on their experience and reviews.',
			'Payment is made only after the user has chosen their preferred specialist.',
			'Before confirming an offer, the user and the specialist have the right to communicate via the Telagri chat to clarify service details.',
			'Users may also receive free consultation offers from specialists. If the user accepts such an offer, no payment is required.',
			'Communication with the selected agronomist via Telagri chat is available for five days after purchasing the service.',
			'The consultation is automatically closed after five days.',
		],
	},
	ru: {
		title: 'Разовая консультационная услуга',
		description:
			'Разовая консультация — это быстрый и эффективный способ получить квалифицированные рекомендации от агрономов. С помощью этой услуги пользователи могут консультироваться со специалистом относительно своих конкретных потребностей. Консультация может касаться существующей проблемы или сосредоточиться на профилактических мерах.',
		howItWorks: 'Как работает услуга?',
		steps: [
			'Фермер выбирает услугу разовой консультации на платформе, указывает культуру и выбирает предпочтительный язык(и) для консультации.',
			'Затем он предоставляет подробное описание проблемы или темы, по которой ему нужна консультация.',
			'Запрос отправляется всем специалистам на платформе с учетом предпочтений по культуре и языку.',
			'Альтернативно, запрос может быть отправлен напрямую конкретному специалисту с использованием уникального индивидуального кода.',
			'Специалисты оценивают сложность запроса и делают соответствующие предложения.',
			'Цена разовой консультации определяется агрономом.',
			'Фермер выбирает специалиста исходя из его опыта и отзывов.',
			'Оплата производится только после того, как пользователь выбрал предпочтительного специалиста.',
			'Перед подтверждением предложения пользователь и специалист имеют право общаться через чат Telagri для уточнения деталей услуги.',
			'Пользователи также могут получать предложения бесплатных консультаций от специалистов. Если пользователь принимает такое предложение, оплата не требуется.',
			'Общение с выбранным агрономом через чат Telagri доступно в течение пяти дней после приобретения услуги.',
			'Консультация автоматически закрывается через пять дней.',
		],
	},
	es: {
		title: 'Servicio de Consulta Única',
		description:
			'Una consulta única es una forma rápida y efectiva de recibir recomendaciones calificadas de agrónomos. A través de este servicio, los usuarios pueden consultar a un especialista sobre sus necesidades específicas. La consulta puede abordar un problema existente o centrarse en medidas preventivas.',
		howItWorks: '¿Cómo Funciona el Servicio?',
		steps: [
			'El agricultor selecciona el servicio de consulta única en la plataforma, especifica el cultivo y elige el/los idioma(s) preferido(s) para la consulta.',
			'A continuación, proporciona una descripción detallada del problema o el tema sobre el que necesita consulta.',
			'La solicitud se envía a todos los especialistas en la plataforma, filtrada según las preferencias de cultivo e idioma.',
			'Alternativamente, la solicitud puede enviarse directamente a un especialista específico utilizando un código individual único.',
			'Los especialistas evalúan la complejidad de la solicitud y presentan ofertas relevantes.',
			'El precio de una consulta única lo determina el agrónomo.',
			'El agricultor selecciona un especialista basándose en su experiencia y reseñas.',
			'El pago se realiza solo después de que el usuario haya elegido su especialista preferido.',
			'Antes de confirmar una oferta, el usuario y el especialista tienen derecho a comunicarse a través del chat de Telagri para aclarar los detalles del servicio.',
			'Los usuarios también pueden recibir ofertas de consulta gratuita de especialistas. Si el usuario acepta tal oferta, no se requiere pago.',
			'La comunicación con el agrónomo seleccionado a través del chat de Telagri está disponible durante cinco días después de comprar el servicio.',
			'La consulta se cierra automáticamente después de cinco días.',
		],
	},
	hy: {
		title: 'Սեզոնային խորհրդատվության ծառայություն',
		description:
			'Սեզոնային խորհրդատվությունը ապահովում է երկարաժամկետ աջակցություն մասնագետի կողմից: Այն նախատեսված է այն ֆերմերների համար, ովքեր պահանջում են կանոնավոր խորհրդատվություններ խնդիրների լուծման, կանխարգելման, վնասատուների և հիվանդությունների կառավարման, ոռոգման և ագրոտեխնիկական գործընթացների վերաբերյալ: Այս ծառայության շրջանակներում օգտատերերը կարող են սահմանել իրենց նախընտրելի խորհրդատվության ժամանակահատվածները և ստանալ համապատասխան խորհուրդներ ամբողջ սեզոնի ընթացքում:',
		howItWorks: 'Ինչպես է աշխատում ծառայությունը',
		steps: [
			'Օգտատերն ընտրում է իր նախընտրելի խորհրդատվության ժամանակահատվածը հարթակում:',
			'Նշում է մշակաբույսը և լեզուները, որոնցով ցանկանում է ստանալ խորհրդատվությունը:',
			'Տրամադրում է մանրամասն տեղեկություններ իր ֆերմայի, խնդրի կամ թեմայի մասին, որի վերաբերյալ խորհրդատվության կարիք ունի:',
			'Հարցումն այնուհետև ուղարկվում է հարթակի բոլոր մասնագետներին՝ ֆիլտրված ըստ մշակաբույսի և լեզվի նախապատվությունների:',
			'Այլընտրանքային տարբերակով, հարցումը կարող է ուղարկվել ուղղակիորեն կոնկրետ մասնագետին՝ օգտագործելով եզակի անհատական կոդ:',
			'Մասնագետները գնահատում են հարցման բովանդակությունն ու ժամկետները՝ նախքան իրենց առաջարկները ներկայացնելը:',
			'Ֆերմերն ընտրում է մասնագետին՝ հիմնվելով նրա փորձի և կարծիքների վրա:',
			'Սեզոնային խորհրդատվության գինը որոշվում է ագրոնոմի կողմից:',
			'Օգտատերը վճարումը կատարում է միայն նախընտրելի մասնագետին ընտրելուց հետո:',
			'Առաջարկը հաստատելուց առաջ օգտատերը և մասնագետը իրավունք ունեն հաղորդակցվել Telagri-ի չաթի միջոցով՝ պարզաբանելու ծառայության մանրամասները:',
			'Ընտրված ագրոնոմի հետ հաղորդակցությունը Telagri-ի չաթի միջոցով հասանելի է ամբողջ ընտրված խորհրդատվության ժամանակահատվածում:',
			'Խորհրդատվությունն ավտոմատ կերպով ավարտվում է ընտրված ժամանակահատվածի ավարտին:',
		],
	},
};

const seasonalContent = {
	ka: {
		title: 'სეზონური კონსულტაციის სერვისის აღწერა',
		description:
			'სეზონური კონსულტაცია გთავაზობთ სპეციალისტის გრძელვადიან მხარდაჭერის სერვისს. სერვისი განკუთვნილია ფერმერებისთვის, რომელთაც სჭირდებათ რეგულარული კონსულტაციები პრობლემების, პრევენცის, მავნებელ-დაავადებების, ირიგიაციის და აგროტექნიკური პროცესების მართვის შესახებ. სერვისის ფარგლებში მომხმარებელს შეუძლია თავად განსაზღვროს კონსულტაციის სასურველი ვადები და მიიღოს შესაბამისი რეკომენდაციები სეზონის განმავლობაში.',
		howItWorks: 'როგორ მუშაობს სერვისი?',
		steps: [
			'მომხმარებელი პლატფორმაზე თავად ირჩევს კონსულტაციისთვის სასურველ პერიოდს.',
			'უთითებს კულტურას და იმ ენებს, რა ენებზეც სურს კონსულტაციის მიღება.',
			'მაქსიმალურად დეტალურად აღწერს ფერმის შესახებ ინფორმაციას,  პრობლემას ან საკითხის შინარსს, რომელზეც სჭირდება კონსულტაცია.',
			'მომხმარებლის მოთხოვნა, კულტურის და ენის ფილტრის გათვალისწინებით, ეგზავნებათ პლატფორმაზე არსებულ ყველა სპეციალისტს.',
			'მოთხოვნის გაგზავნა ასევე შესაძლებელია პირდაპირ კონკრეტულ სპეციალისტთან, სპეციალური ინდივიდუალური კოდის მეშვეობით.',
			'სპეციალისტები შეაფასაბენ მოთხოვნის შინაარსს, ვადებს და ამის შემდეგ აგზავნიან შესაბამის შეთავაზებებს.',
			'ფერმერი თავად ირჩევს სპეციალისტს მისი გამოცდილებისა და შეფასებების მიხედვით.',
			'სეზონური კონსულტაციის ღირებულებას განსაზღვრავს თავად აგრნონომი. ',
			'მომხმარებელი თანხას მას შემდეგ იხდის, რაც შეარჩევს მისთვის სასურველ სპეციალისტს.',
			'მომხმარებელს და სპეციალისტს აქვთ უფლება წინასწარ, შეთავაზების დადასტურებამდე, ჰქონდეთ სერვისის შესახებ კომუნიკაცია ტელაგრის ჩატში, სერვისის ფარგლებში სხვადასხვა დეტალების დასაზუსტებლად.',
			'აგრონომთან კომუნიკაცია ტელაგრის ჩატის მეშვეობით შესაძლებელია შერჩეული პერიოდის განმავლობაში.',
			'კონსულტაცია სრულდება, შერჩეული პერიოდის ვადის ამოწურვისთანავე.',
		],
	},
	en: {
		title: 'Seasonal Consultation Service',
		description:
			'The seasonal consultation service provides long-term support from a specialist. It is designed for farmers who require regular consultations on problem-solving, prevention, pest and disease management, irrigation, and agro-technical processes. Within this service, users can define their preferred consultation periods and receive relevant recommendations throughout the season.',
		howItWorks: 'How Does the Service Work?',
		steps: [
			'The user selects their preferred consultation period on the platform.',
			'They specify the crop and the languages in which they wish to receive the consultation.',
			'They provide detailed information about their farm, the problem, or the topic they need consultation on.',
			'The request is then sent to all specialists on the platform, filtered based on crop and language preferences.',
			'Alternatively, the request can be sent directly to a specific specialist using a unique individual code.',
			"Specialists assess the request's content and timeframe before submitting their offers.",
			'The farmer selects a specialist based on their experience and reviews.',
			'The price for seasonal consultation is determined by the agronomist.',
			'The user makes the payment only after selecting their preferred specialist.',
			'Before confirming an offer, the user and the specialist have the right to communicate via Telagri chat to clarify service details.',
			'Communication with the selected agronomist via Telagri chat is available throughout the chosen consultation period.',
			'The consultation automatically ends once the selected period expires.',
		],
	},
	es: {
		title: 'Servicio de Consulta de Temporada',
		description:
			'El servicio de consulta de temporada proporciona apoyo a largo plazo de un especialista. Está diseñado para agricultores que requieren consultas regulares sobre resolución de problemas, prevención, manejo de plagas y enfermedades, riego y procesos agrotécnicos. Con este servicio, los usuarios pueden definir sus períodos de consulta preferidos y recibir recomendaciones relevantes durante toda la temporada.',
		howItWorks: '¿Cómo Funciona el Servicio?',
		steps: [
			'El usuario selecciona su período de consulta preferido en la plataforma.',
			'Especifica el cultivo y los idiomas en los que desea recibir la consulta.',
			'Proporciona información detallada sobre su granja, el problema o el tema sobre el que necesita consulta.',
			'La solicitud se envía a todos los especialistas en la plataforma, filtrada según las preferencias de cultivo e idioma.',
			'Alternativamente, la solicitud puede enviarse directamente a un especialista específico utilizando un código individual único.',
			'Los especialistas evalúan el contenido y el plazo de la solicitud antes de presentar sus ofertas.',
			'El agricultor selecciona un especialista basándose en su experiencia y reseñas.',
			'El precio de la consulta de temporada lo determina el agrónomo.',
			'El usuario realiza el pago solo después de seleccionar su especialista preferido.',
			'Antes de confirmar una oferta, el usuario y el especialista tienen derecho a comunicarse a través del chat de Telagri para aclarar los detalles del servicio.',
			'La comunicación con el agrónomo seleccionado a través del chat de Telagri está disponible durante todo el período de consulta elegido.',
			'La consulta finaliza automáticamente una vez que expire el período seleccionado.',
		],
	},
	ru: {
		title: 'Сезонная консультационная услуга',
		description:
			'Сезонная консультация обеспечивает долгосрочную поддержку от специалиста. Она предназначена для фермеров, которым требуются регулярные консультации по решению проблем, профилактике, борьбе с вредителями и болезнями, орошению и агротехническим процессам. В рамках этой услуги пользователи могут определить предпочтительные периоды консультаций и получать соответствующие рекомендации в течение всего сезона.',
		howItWorks: 'Как работает услуга?',
		steps: [
			'Пользователь выбирает предпочтительный период консультации на платформе.',
			'Указывает культуру и языки, на которых он хочет получить консультацию.',
			'Предоставляет подробную информацию о своей ферме, проблеме или теме, по которой ему нужна консультация.',
			'Запрос отправляется всем специалистам на платформе с учетом предпочтений по культуре и языку.',
			'Альтернативно, запрос может быть отправлен напрямую конкретному специалисту с использованием уникального индивидуального кода.',
			'Специалисты оценивают содержание и сроки запроса перед представлением своих предложений.',
			'Фермер выбирает специалиста исходя из его опыта и отзывов.',
			'Цена сезонной консультации определяется агрономом.',
			'Пользователь производит оплату только после выбора предпочтительного специалиста.',
			'Перед подтверждением предложения пользователь и специалист имеют право общаться через чат Telagri для уточнения деталей услуги.',
			'Общение с выбранным агрономом через чат Telagri доступно в течение всего выбранного периода консультации.',
			'Консультация автоматически завершается по истечении выбранного периода.',
		],
	},
	hy: {
		title: 'Սեզոնային խորհրդատվության ծառայություն',
		description:
			'Սեզոնային խորհրդատվությունը ապահովում է երկարաժամկետ աջակցություն մասնագետի կողմից: Այն նախատեսված է այն ֆերմերների համար, ովքեր պահանջում են կանոնավոր խորհրդատվություններ խնդիրների լուծման, կանխարգելման, վնասատուների և հիվանդությունների կառավարման, ոռոգման և ագրոտեխնիկական գործընթացների վերաբերյալ: Այս ծառայության շրջանակներում օգտատերերը կարող են սահմանել իրենց նախընտրելի խորհրդատվության ժամանակահատվածները և ստանալ համապատասխան խորհուրդներ ամբողջ սեզոնի ընթացքում:',
		howItWorks: 'Ինչպես է աշխատում ծառայությունը',
		steps: [
			'Օգտատերն ընտրում է իր նախընտրելի խորհրդատվության ժամանակահատվածը հարթակում:',
			'Նշում է մշակաբույսը և լեզուները, որոնցով ցանկանում է ստանալ խորհրդատվությունը:',
			'Տրամադրում է մանրամասն տեղեկություններ իր ֆերմայի, խնդրի կամ թեմայի մասին, որի վերաբերյալ խորհրդատվության կարիք ունի:',
			'Հարցումն այնուհետև ուղարկվում է հարթակի բոլոր մասնագետներին՝ ֆիլտրված ըստ մշակաբույսի և լեզվի նախապատվությունների:',
			'Այլընտրանքային տարբերակով, հարցումը կարող է ուղարկվել ուղղակիորեն կոնկրետ մասնագետին՝ օգտագործելով եզակի անհատական կոդ:',
			'Մասնագետները գնահատում են հարցման բովանդակությունն ու ժամկետները՝ նախքան իրենց առաջարկները ներկայացնելը:',
			'Ֆերմերն ընտրում է մասնագետին՝ հիմնվելով նրա փորձի և կարծիքների վրա:',
			'Սեզոնային խորհրդատվության գինը որոշվում է ագրոնոմի կողմից:',
			'Օգտատերը վճարումը կատարում է միայն նախընտրելի մասնագետին ընտրելուց հետո:',
			'Առաջարկը հաստատելուց առաջ օգտատերը և մասնագետը իրավունք ունեն հաղորդակցվել Telagri-ի չաթի միջոցով՝ պարզաբանելու ծառայության մանրամասները:',
			'Ընտրված ագրոնոմի հետ հաղորդակցությունը Telagri-ի չաթի միջոցով հասանելի է ամբողջ ընտրված խորհրդատվության ժամանակահատվածում:',
			'Խորհրդատվությունն ավտոմատ կերպով ավարտվում է ընտրված ժամանակահատվածի ավարտին:',
		],
	},
};

export function FindSpecialistModal({ open, onClose, description }: FindSpecialistModalProps) {
	const modalDimensions = useModalDimensions();
	const { i18n, t } = useTranslation();
	const currentLanguage = i18n.language as 'ka' | 'en' | 'ru';

	return (
		<GeneralMuiModal
			maxWidth={'xl'}
			title={t('servicesCards.oneTime.title')}
			styles={{
				paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },
				contentStyle: { padding: 0 },
			}}
			open={open}
			onClose={onClose}
			content={
				<>
					<div className="flex flex-col gap-4 p-[20px]">
						<div>
							<p className="text-black font-roboto text-base font-normal leading-[140%] mb-10">
								{description}
							</p>

							<h3 className="text-black font-roboto text-base font-semibold leading-[140%]">
								{oneTimeContent[currentLanguage].title}
							</h3>

							<p className="text-black font-roboto text-base font-normal leading-[140%]">
								{oneTimeContent[currentLanguage].description}
							</p>

							<h3 className="text-black font-roboto text-base font-semibold leading-[140%] mt-[20px]">
								{oneTimeContent[currentLanguage].howItWorks}
							</h3>

							<ul className="list-disc list-inside flex flex-col gap-[20px] pb-5 pl-[20px] mt-[20px]">
								{oneTimeContent[currentLanguage].steps.map((step, index) => (
									<li
										key={index}
										className="text-black font-roboto text-base font-normal leading-[140%]"
									>
										{step}
									</li>
								))}
							</ul>
						</div>
					</div>

					<div className="flex flex-col gap-4 p-[20px]">
						<div>
							<h3 className="text-black font-roboto text-base font-semibold leading-[140%]">
								{seasonalContent[currentLanguage].title}
							</h3>

							<p className="text-black font-roboto text-base font-normal leading-[140%]">
								{seasonalContent[currentLanguage].description}
							</p>

							<h3 className="text-black font-roboto text-base font-semibold leading-[140%] mt-[20px]">
								{seasonalContent[currentLanguage].howItWorks}
							</h3>

							<ul className="list-disc list-inside flex flex-col gap-[20px] pb-5 pl-[20px] mt-[20px]">
								{seasonalContent[currentLanguage].steps.map((step, index) => (
									<li
										key={index}
										className="text-black font-roboto text-base font-normal leading-[140%]"
									>
										{step}
									</li>
								))}
							</ul>
						</div>
					</div>
				</>
			}
		/>
	);
}
